import { format } from "date-fns";
import { createContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AlertWrapper from "~/src/components/Alerts/AlertWrapper";
import BottomBar from "~/src/components/BottomBars/BottomBars";
import BottomLine from "~/src/components/BottomLine";
import Counter from "~/src/components/Counter/Counter";
import { SaveIcon } from "~/src/components/Icons/Icons";
import { FormDataContext } from "~/src/contexts/FormDataContext/FormDataContext";
import { ArtType } from "~/src/helpers/types";
import { useStateful } from "~/src/hooks/useStateful";
import { Terreng } from "~/src/pages/Registrering/types";
import { AccordionDefault } from "../../../../components/Accordion/AccordionDefault";
import AlertDefault, { AlertVariant } from "../../../../components/Alerts/AlertDefault";
import ButtonDefault, { ButtonVariant } from "../../../../components/Button/ButtonDefault";
import { dateFormatNorsk } from "../../../../components/CalendarPicker/CalendarConstants";
import CalendarPicker from "../../../../components/CalendarPicker/CalendarPicker";
import Card from "../../../../components/Card/Card";
import JaktomradePicker from "../../../../components/JaktomradePicker/JaktomradePicker";
import AntallJegereOgTimerJaktet from "../common/AntallJegereOgTimerJaktet";
import { RegistrerJaktdagProps } from "../common/types";
import { setTerreng, sumSettOgSkuttHjort } from "./HjortHelpers";
import {
  JaktdagRegistrationDataHjort,
  TermiskMap,
  generateTermiskData,
  settHjortInnmarkDataKeys,
  settHjortUtmarkDataKeys,
  skuttHjortInnmarkDataKeys,
  skuttHjortUtmarkDataKeys,
  skuttHjortTypes,
} from "./HjortTypes";
import TermiskRegistrering from "./TermiskRegistrering";
import useRegistrerHjort from "./useRegistrerHjort";
import VelgTerreng from "./VelgTerreng";

export const JaktdagHjortFormDataContext = createContext<
  FormDataContext<JaktdagRegistrationDataHjort> | undefined
>(undefined);

export default function RegistrerHjort({ initialData }: RegistrerJaktdagProps): JSX.Element {
  const { t } = useTranslation();
  const scrollTopRef = useRef<HTMLDivElement>(null);
  const {
    jaktdagEksistererDialog,
    eksisterendeJaktdag,
    isSubmitting,
    successDialog,
    counterErrorMessages,
    handleSubmit,
    handleChangeJaktomrade,
    formData,
    formErrors,
    setFormData,
    setFormErrors,
    selectedJaktomrade,
    date,
    fellingsRapportLevert,
    terrengHarRegistreringerDialog,
    loadingEksisterendeJaktdager,
    hasGodkjenteDyr,
  } = useRegistrerHjort(initialData, scrollTopRef);
  const innmarkSettAccordionOpen = useStateful<boolean>(formData?.Innmark || false);
  const innmarkSkuttAccordionOpen = useStateful<boolean>(formData?.Innmark || false);
  const utmarkSettAccordionOpen = useStateful<boolean>(formData?.Utmark || false);
  const utmarkSkuttAccordionOpen = useStateful<boolean>(formData?.Utmark || false);
  const sumSettOgSkutt = sumSettOgSkuttHjort(formData);

  const labelFor = (key: string) =>
    t(`pages.registrerArt.hjort.skutt.${key}`, { defaultValue: key });

  const buildInitialTermiskMap = (): TermiskMap => {
    const map: TermiskMap = {};
    for (const slot of generateTermiskData(formData, labelFor)) {
      map[slot.key] = null;
    }
    if (initialData?.Individer) {
      const indeksPerType: Record<string, number> = {};
      for (const type of skuttHjortTypes) {
        const existingForType = initialData.Individer.filter(
          (i) => i.Alder === type.alder && i.Kjønn === type.kjønn && i.Terreng === type.terreng,
        );
        indeksPerType[type.key] = 0;
        for (const individ of existingForType) {
          const slotKey = `${type.key}-${indeksPerType[type.key]}`;
          if (slotKey in map) {
            map[slotKey] = individ.Termisk ?? null;
          }
          indeksPerType[type.key]++;
        }
      }
    }
    return map;
  };

  const [termiskMap, setTermiskMap] = useState<TermiskMap>(buildInitialTermiskMap);

  const [triedToSubmit, setTriedToSubmit] = useState(false);

  const skuttCounterDeps = skuttHjortTypes.map(
    (t) => (formData[t.key as keyof JaktdagRegistrationDataHjort] as number) || 0,
  );
  useEffect(() => {
    const newSlots = generateTermiskData(formData, labelFor);
    setTermiskMap((prev) => {
      const next: TermiskMap = {};
      for (const type of skuttHjortTypes) {
        const typeKey = type.key;
        const newSlotsForType = newSlots.filter((s) => s.dyrTypeKey === typeKey);
        const newCount = newSlotsForType.length;
        const oldCount = Object.keys(prev).filter((k) => k.startsWith(`${typeKey}-`)).length;

        if (newCount < oldCount) {
          const oldValues = Array.from({ length: oldCount }, (_, i) => prev[`${typeKey}-${i}`]);
          const answeredValues = oldValues.filter(
            (v): v is boolean => v !== null && v !== undefined,
          );
          const hasMixedValues =
            answeredValues.some((v) => v === true) && answeredValues.some((v) => v === false);

          if (hasMixedValues) {
            for (const slot of newSlotsForType) {
              next[slot.key] = null;
            }
          } else {
            for (const slot of newSlotsForType) {
              next[slot.key] = slot.key in prev ? prev[slot.key] : null;
            }
          }
        } else {
          for (const slot of newSlotsForType) {
            next[slot.key] = slot.key in prev ? prev[slot.key] : null;
          }
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, skuttCounterDeps);

  const termiskData = useMemo(
    () => generateTermiskData(formData, labelFor),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    skuttCounterDeps,
  );

  const totalSkutt = skuttHjortTypes.reduce(
    (sum, type) => sum + ((formData[type.key as keyof JaktdagRegistrationDataHjort] as number) || 0),
    0,
  );
  const termiskFerdig =
    totalSkutt === 0 ||
    termiskData.every((slot) => termiskMap[slot.key] !== null && termiskMap[slot.key] !== undefined);

  const handleTermiskChange = (key: string, value: boolean) => {
    setTermiskMap((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmitMedTermisk = (e: React.SyntheticEvent) => {
    setTriedToSubmit(true);
    if (!termiskFerdig) {
      e.preventDefault();
      return;
    }
    return handleSubmit(e, termiskMap);
  };

  return (
    <JaktdagHjortFormDataContext.Provider
      value={{
        formData: formData as JaktdagRegistrationDataHjort,
        formErrors,
        setFormData,
        setFormErrors: setFormErrors,
      }}
    >
      <div ref={scrollTopRef} />
      <div className="mx-4">
        <AlertWrapper className="mt-4">
          {fellingsRapportLevert.value && (
            <AlertDefault id="registreringFellingsrapportLevertAlert" variant={AlertVariant.Info}>
              {t("pages.registrering.alerts.fellingsrapportLevert")}
            </AlertDefault>
          )}
          {formErrors.Id && (
            <AlertDefault variant={AlertVariant.Error}>{formErrors.Id}</AlertDefault>
          )}
          {!!eksisterendeJaktdag.value && (
            <AlertDefault variant={AlertVariant.Warning}>
              {t("pages.registrering.errors.jaktdagEksisterer")}
            </AlertDefault>
          )}
        </AlertWrapper>
      </div>
      <form className="h-full flex pt-4 flex-col justify-between">
        <Card color="transparent" shadow={false} className="mx-4 mb-6">
          <AccordionDefault
            title={t("pages.registrerArt.datoOgJaktfelt")}
            details={
              formData.jaktomradeNavn &&
              date.value &&
              format(date.value, dateFormatNorsk) + " " + formData.jaktomradeNavn
            }
            error={Boolean(
              formErrors.Jaktfelt || formErrors.AntallJegere || formErrors.AntallTimerJaktet,
            )}
            info={
              hasGodkjenteDyr ? t("pages.registrerArt.kanIkkeEndreDatoOgJaktomrade") : undefined
            }
          >
            <CalendarPicker
              id="registrerArtDatePicker"
              label={hasGodkjenteDyr ? t("pages.registrerArt.dato") : t("form.fields.date.label")}
              date={date}
              readOnly={fellingsRapportLevert?.value || hasGodkjenteDyr}
              errorText={formErrors.Dato}
            />
            {!(initialData === undefined) && <BottomLine className="mb-2" />}
            <JaktomradePicker
              className="mt-4"
              selectedState={selectedJaktomrade}
              valgtArt={ArtType.Hjort}
              onChange={async (selected) => handleChangeJaktomrade(selected)}
              readOnly={fellingsRapportLevert?.value || hasGodkjenteDyr}
              defaultOmradekode={formData.Jaktfelt || initialData?.Jaktfelt || ""}
              helperText={hasGodkjenteDyr ? undefined : t("form.fields.jaktfelt.helperText")}
              errorText={formErrors.Jaktfelt}
            />

            <BottomLine className="mt-4" />

            <VelgTerreng
              innmark={!!formData.Innmark}
              utmark={!!formData.Utmark}
              setInnmark={(value) => {
                setTerreng(
                  value,
                  Terreng.Innmark,
                  formData,
                  setFormData,
                  terrengHarRegistreringerDialog,
                  innmarkSkuttAccordionOpen,
                  innmarkSettAccordionOpen,
                );
              }}
              setUtmark={(value) => {
                setTerreng(
                  value,
                  Terreng.Utmark,
                  formData,
                  setFormData,
                  terrengHarRegistreringerDialog,
                  utmarkSkuttAccordionOpen,
                  utmarkSettAccordionOpen,
                );
              }}
              errors={!!(formErrors.Innmark || formErrors.Utmark)}
            />
          </AccordionDefault>
          <AccordionDefault
            count={sumSettOgSkutt.innmark.sett.sumSett}
            isOpen={innmarkSettAccordionOpen}
            title={t("pages.registrerArt.sett")}
            details={t("pages.fellingsinfo.innmark")}
            disabled={fellingsRapportLevert.value || !formData.Innmark}
            errorMessage={counterErrorMessages.settInnmark}
            info={t("pages.registrerArt.settHelpText", {
              art: t("fauna.art.hjort.artsnavn").toLowerCase(),
            })}
            useErrorIcon
          >
            <AntallJegereOgTimerJaktet
              formData={formData}
              setFormData={setFormData}
              errors={formErrors}
              setErrors={setFormErrors}
              disabled={fellingsRapportLevert.value}
              innmark={true}
            />
            <div className="mt-4" />
            {settHjortInnmarkDataKeys.map((dyrType) => {
              return (
                <Counter
                  key={dyrType}
                  id={dyrType}
                  label={t(`pages.registrerArt.hjort.sett.${dyrType}`)}
                  error={
                    formErrors[dyrType]
                      ? t("form.fields.settAntall.validationErrorShort")
                      : undefined
                  }
                  value={formData[dyrType] || 0}
                  onCountChanged={(count) => {
                    const _formData = { ...formData };
                    _formData[dyrType] = count;
                    setFormData({ ..._formData });
                  }}
                />
              );
            })}
            <BottomLine />
          </AccordionDefault>
          <AccordionDefault
            count={sumSettOgSkutt.innmark.skutt.sumSkutt}
            isOpen={innmarkSkuttAccordionOpen}
            title={t("pages.registrerArt.skutt")}
            details={t("pages.fellingsinfo.innmark")}
            disabled={fellingsRapportLevert.value || !formData.Innmark}
            errorMessage={counterErrorMessages.skuttInnmark}
            info={
              !counterErrorMessages.skuttInnmark
                ? t("form.fields.skuttAntall.validationError")
                : undefined
            }
            useErrorIcon
          >
            {skuttHjortInnmarkDataKeys.map((dyrType) => {
              return (
                <Counter
                  key={dyrType}
                  id={dyrType}
                  label={t(`pages.registrerArt.hjort.skutt.${dyrType}`)}
                  error={
                    formErrors[dyrType]
                      ? t("form.fields.skuttAntall.validationErrorShort")
                      : undefined
                  }
                  value={formData[dyrType] || 0}
                  onCountChanged={(count) => {
                    const _formData = { ...formData };
                    _formData[dyrType] = count;
                    setFormData({ ..._formData });
                  }}
                />
              );
            })}
            <BottomLine />
          </AccordionDefault>
          <AccordionDefault
            count={sumSettOgSkutt.utmark.sett.sumSett}
            isOpen={utmarkSettAccordionOpen}
            title={t("pages.registrerArt.sett")}
            details={t("pages.fellingsinfo.utmark")}
            disabled={fellingsRapportLevert.value || !formData.Utmark}
            errorMessage={counterErrorMessages.settUtmark}
            info={t("pages.registrerArt.settHelpText", {
              art: t("fauna.art.hjort.artsnavn").toLowerCase(),
            })}
            useErrorIcon
          >
            <AntallJegereOgTimerJaktet
              formData={formData}
              setFormData={setFormData}
              errors={formErrors}
              setErrors={setFormErrors}
              disabled={fellingsRapportLevert.value}
            />
            <div className="mb-4" />
            {settHjortUtmarkDataKeys.map((dyrType) => {
              return (
                <Counter
                  key={dyrType}
                  id={dyrType}
                  label={t(`pages.registrerArt.hjort.sett.${dyrType}`)}
                  error={
                    formErrors[dyrType]
                      ? t("form.fields.settAntall.validationErrorShort")
                      : undefined
                  }
                  value={formData[dyrType] || 0}
                  onCountChanged={(count) => {
                    const _formData = { ...formData };
                    _formData[dyrType] = count;
                    setFormData({ ..._formData });
                  }}
                />
              );
            })}
            <BottomLine />
          </AccordionDefault>
          <AccordionDefault
            count={sumSettOgSkutt.utmark.skutt.sumSkutt}
            isOpen={utmarkSkuttAccordionOpen}
            title={t("pages.registrerArt.skutt")}
            details={t("pages.fellingsinfo.utmark")}
            disabled={fellingsRapportLevert.value || !formData.Utmark}
            errorMessage={counterErrorMessages.skuttUtmark}
            info={
              !counterErrorMessages.skuttUtmark
                ? t("form.fields.skuttAntall.validationError")
                : undefined
            }
            useErrorIcon
          >
            {skuttHjortUtmarkDataKeys.map((dyrType) => {
              return (
                <Counter
                  key={dyrType}
                  id={dyrType}
                  label={t(`pages.registrerArt.hjort.skutt.${dyrType}`)}
                  error={
                    formErrors[dyrType]
                      ? t("form.fields.skuttAntall.validationErrorShort")
                      : undefined
                  }
                  value={formData[dyrType] || 0}
                  onCountChanged={(count) => {
                    const _formData = { ...formData };
                    _formData[dyrType] = count;
                    setFormData({ ..._formData });
                  }}
                />
              );
            })}
            <BottomLine />
          </AccordionDefault>
          <TermiskRegistrering
            slots={termiskData}
            termiskMap={termiskMap}
            onChange={handleTermiskChange}
            disabled={fellingsRapportLevert.value}
            showValidation={triedToSubmit}
          />
          {successDialog.element}
          {jaktdagEksistererDialog.element}
          {terrengHarRegistreringerDialog.element}
        </Card>

        <BottomBar>
          <ButtonDefault
            id="registrerHjortLagreButton"
            label={t("actions.save")}
            variant={ButtonVariant.PrimaryDarkBg}
            icon={<SaveIcon />}
            type="button"
            onClick={(e) => handleSubmitMedTermisk(e)}
            loading={isSubmitting}
            disabled={
              fellingsRapportLevert.value ||
              !!eksisterendeJaktdag.value ||
              loadingEksisterendeJaktdager ||
              isSubmitting
            }
          />
        </BottomBar>
      </form>
    </JaktdagHjortFormDataContext.Provider>
  );
}
